<template>
  <div class="exam-choice-component" v-invalid="validation.is_valid">
    <div class="field">
      <label class="label"
             v-if="field.label"
             :for="field.id">
        <strong class="has-text-grey-dark">{{ field.label }}</strong>
      </label>
      <button class="button is-outlined is-large" @click="showModal()" :disabled="!enabledButton" v-translate>
        Choose
      </button>
      <p class="help is-danger"
         v-if="!enabledButton"
         v-translate>
        You need to select a qualification before you can select an exam!
      </p>
      <card-modal-component
        :show="show_modal"
        @deactivate="hideModal()"
        transition="fade"
        ref="modal"
        :options="{ hasOkBtn: { text: 'Choose' }, hasCancelBtn: { enabled: true }}"
        @handleOk="chooseExamOption($event)"
        @handleCancel="hideModal()"
        :title="'Exam selection'">
        <div slot="card-content" class="field">
          <div class="control">
            <ul v-if="field.layout.layout_type === 'vertical' && !!exam_options.length">
              <li v-for="(option, key) in exam_options" :key="key">
                <label class="radio" ref="label">
                  <input type="radio"
                         v-model="formData.fields[field.id].value"
                         @change="formData.inputOnChange(field); removeValidation()"
                         :id="option.id"
                         :name="field.id"
                         :value="option.option_value"
                         :disabled="field.properties.input_readonly">
                  <span class="check"></span>
                  <span class="control-label">{{ option.display_name ? option.display_name : option.option_value }}</span>
                </label>
              </li>
            </ul>
            <div v-if="field.layout.layout_type === 'horizontal' && !!exam_options.length">
              <label v-for="(option, key) in exam_options" :key="key" class="radio" >
                <input type="radio"
                       v-model="formData.fields[field.id].value"
                       @change="formData.inputOnChange(field); removeValidation()"
                       :id="option.id"
                       :name="field.id"
                       :value="option.option_value"
                       :disabled="field.properties.input_readonly">
                <span class="check"></span>
                <span class="control-label">{{ option.display_name ? option.display_name : option.option_value }}</span>
              </label>
            </div>
            <div v-if="is_loading" key="loading_block">
              <p><translate>Loading available exams. Please wait...</translate></p>
            </div>

            <div v-if="!exam_options.length && !is_loading" key="no_exams_block">
              <p v-translate>There are no available exams. If you think this is a mistake, please contact us at se.bygg@kiwa.com or 0455-305600.</p>
            </div>

            <p class="help is-danger"
               v-if="!validation.is_valid" v-translate>
              {{ validation.message }}
            </p>
            <p class="help"
               v-if="field.properties.help_text"
               v-html="field.properties.help_text">
            </p>
          </div>
        </div>
      </card-modal-component>
    </div>
  </div>
</template>

<script>
  import FormField from '@/classes/form/FormField'
  import FormData from '@/classes/form/FormData'
  import { formHandlerMixin } from '@/mixins/mixins'
  import { mapActions, mapState } from 'vuex'
  import CardModalComponent from '@/components/layout/modal/CardModalComponent'

  export default {
    name: 'exam-choice-component',
    mixins: [formHandlerMixin],
    components: {
      CardModalComponent
    },
    data () {
      return {
        show_modal: false,
        exam_options: [],
        is_loading: false
      }
    },
    props: {
      field: {
        type: FormField
      },
      formData: {
        type: FormData
      }
    },
    computed: {
      ...mapState({
        formDatas: state => state.formModule.formDatas,
        applicationCode: state => state.formModule.service.code
      }),
      computedFormDatas () {
        let applicationFormDatas = this.formDatas
        if (applicationFormDatas.length !== 0) {
          return applicationFormDatas
        }
        return [this.formData]
      },
      validation () {
        return this.formData.validations[this.field.id] || { is_valid: true }
      },
      kaLevelValue () {
        let result = null
        this.computedFormDatas.forEach(formData => {
          for (let key in formData.fields) {
            if (key.includes('ka_level')) {
              result = formData.fields[key].value
            }
          }
        })
        return result
      },
      popCategoryValue () {
        let result = null
        this.computedFormDatas.forEach(formData => {
          for (let key in formData.fields) {
            if (key.includes('pop_category')) {
              result = formData.fields[key].value
            }
          }
        })
        return result
      },
      certificationAreasValue () {
        let result = null
        this.computedFormDatas.forEach(formData => {
          for (let key in formData.fields) {
            if (key.includes('certification_areas')) {
              result = formData.fields[key].value
            }
          }
        })
        return result
      },
      presentQualificationFields () {
        let result = null
        this.computedFormDatas.forEach(formData => {
          for (let key in formData.fields) {
            if (key.includes('certification_areas') || key.includes('pop_category') || key.includes('ka_level')) {
              result = true
            }
          }
        })
        return result
      },
      enabledButton () {
        return this.kaLevelValue || this.popCategoryValue || this.certificationAreasValue || !this.presentQualificationFields
      },
      selectedExamChoice () {
        let result = null
        this.computedFormDatas.forEach(formData => {
          for (let key in formData.fields) {
            if (key.includes('exam_choices')) {
              result = formData.fields[key].value
            }
          }
        })
        return result
      }
    },
    methods: {
      ...mapActions([
        'getApplicationExams'
      ]),
      removeValidation () {
        this.validation.is_valid = true
      },
      getExams () {
        this.is_loading = true
        this.getApplicationExams(this.applicationCode).then((results) => {
          let options = []
          let sortBy = (location, date) => (a, b) => {
            let locationA = a[location].toLowerCase()
            let locationB = b[location].toLowerCase()
            if (locationA > locationB) {
              return 1
            }
            if (locationA < locationB) {
              return -1
            }
            let dateA = Date.parse(a[date])
            let dateB = Date.parse(b[date])
            if (dateA > dateB) {
              return 1
            }
            return -1
          }
          results.data.forEach((result) => {
            let displayName = result.location + ', ' + result.exam_date
            let option = { 'id': result.id, 'option_value': result.id, 'exam_location': result.location, 'exam_date': result.exam_date, 'display_name': displayName }
            options.push(option)
          })
          this.exam_options = options
          this.exam_options.sort(sortBy('exam_location', 'exam_date'))
          this.is_loading = false
        }).catch((err) => {
          this.is_loading = false
          this.showErrorMessage(err)
        })
      },
      showModal () {
        this.getExams()
        this.show_modal = true
      },
      hideModal () {
        this.show_modal = false
      },
      chooseExamOption (event) {
        if (this.selectedExamChoice !== null) {
          let result = null
          this.exam_options.forEach(option => {
            if (option.id === this.selectedExamChoice) {
              result = option
            }
          })
          this.fillOtherFields(result)
        }
        this.show_modal = false
      },
      fillOtherFields (option) {
        let examDateKey = 'exam_date_auto'
        let examDateField = this.field.id.replace(/@.*?#/, '@' + examDateKey + '#')
        this.formData.fields[examDateField].value = option.exam_date
        let examLocationKey = 'exam_location_auto'
        let examLocationField = this.field.id.replace(/@.*?#/, '@' + examLocationKey + '#')
        this.formData.fields[examLocationField].value = option.exam_location
        let examEventPkKey = 'exam_event_pk_auto'
        let examEventPkField = this.field.id.replace(/@.*?#/, '@' + examEventPkKey + '#')
        this.formData.fields[examEventPkField].value = option.id
      }
    }
  }
</script>

<style lang="sass" scoped>
  .label
    margin-bottom: 1em

  ul
    list-style-position: inside
    li
      margin-bottom: 1.53rem

</style>
